import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { device } from "../../atoms/Utlis/media";

export const gradient = "linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%)";

export const exportColors = {
  copy: "#8e4e8f",
  csv: "#35b7c8",
  excel: "#31a66a",
  pdf: "#e44862",
  print: "#f09a3e",
};

export const spinAnim = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

export const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  box-sizing: border-box;
  overflow: hidden;

  @media ${device.tablet} {
    padding: 18px;
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
  flex-shrink: 0;

  @media ${device.tablet} {
    flex-direction: column;
  }
`;

export const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 34px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const PageSubtitle = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #ffffff;
  cursor: pointer;
  color: #171717;
  transition: all 0.15s ease;
  &:hover {
    background: #f7f7f7;
    transform: translateY(-1px);
  }
`;

export const Panel = styled.div`
  width: 100%;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(23, 23, 23, 0.06);
  overflow: hidden;
  padding: 24px;
  margin-bottom: 24px;
`;

export const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 800;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #171717;
`;

export const ExportRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 0 18px 0;
  flex-wrap: wrap;
  border-bottom: 1px solid rgba(23, 23, 23, 0.08);
  margin-bottom: 20px;
`;

export const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  flex: 1;
`;

export const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;


export const ExportButton = styled.button<{ $variant: keyof typeof exportColors }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-height: 30px;
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: ${(props) => exportColors[props.$variant]};
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s ease;
  &:hover {
    opacity: 0.9;
  }
`;

export const WhatsAppShareButton = styled.button<{ $loading?: boolean; disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 30px;
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  background: ${({ disabled }) =>
    disabled
      ? "rgba(0,0,0,0.12)"
      : "linear-gradient(135deg, #25D366 0%, #128C7E 100%)"};
  color: ${({ disabled }) => (disabled ? "rgba(0,0,0,0.35)" : "#ffffff")};
  font-size: 12px;
  font-weight: 700;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: opacity 0.15s ease, transform 0.15s ease;
  box-shadow: ${({ disabled }) =>
    disabled ? "none" : "0 4px 12px rgba(37, 211, 102, 0.3)"};
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  svg.spin {
    animation: ${spinAnim} 0.9s linear infinite;
  }
`;

export const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 6px;
  background: ${(props) => {
    switch (props.$status?.toLowerCase()) {
      case "approved":
      case "converted":
        return "rgba(49, 166, 106, 0.12)";
      case "rejected":
      case "cancelled":
        return "rgba(228, 72, 98, 0.12)";
      case "sent":
        return "rgba(53, 183, 200, 0.12)";
      default:
        return "rgba(240, 154, 62, 0.12)";
    }
  }};
  color: ${(props) => {
    switch (props.$status?.toLowerCase()) {
      case "approved":
      case "converted":
        return "#31a66a";
      case "rejected":
      case "cancelled":
        return "#e44862";
      case "sent":
        return "#35b7c8";
      default:
        return "#f09a3e";
    }
  }};
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
`;

export const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const InfoLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.55);
`;

export const InfoValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #171717;
`;

export const TableWrap = styled.div`
  width: 100%;
  overflow: auto;
  border-radius: 8px;
  border: 1px solid rgba(23, 23, 23, 0.08);
`;

export const DetailsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;
  background: #ffffff;

  thead tr {
    background: transparent;
  }

  th {
    font-family: Montserrat, sans-serif;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    text-align: left;
    padding: 14px;
  }

  th span {
    background: linear-gradient(144.01deg, #FFC951 0%, #C94DAA 80%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
    display: inline-block;
  }
  td {
    color: rgba(23, 23, 23, 0.82);
    font-family: Montserrat, sans-serif;
    font-size: 13px;
    font-weight: 500;
    padding: 13px 14px;
    border-bottom: 1px solid rgba(23, 23, 23, 0.07);
  }

  tbody tr:hover {
    background: rgba(201, 77, 170, 0.04);
  }
`;

export const SummaryPanel = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

export const SummaryCard = styled.div`
  width: 300px;
  background: #fbf9fb;
  border: 1px solid rgba(23, 23, 23, 0.08);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const SummaryRow = styled.div<{ $highlight?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${props => (props.$highlight ? "16px" : "13px")};
  font-weight: ${props => (props.$highlight ? "800" : "600")};
  color: ${props => (props.$highlight ? "#171717" : "rgba(23, 23, 23, 0.7)")};
  padding-top: ${props => (props.$highlight ? "10px" : "0")};
  border-top: ${props => (props.$highlight ? "1px dashed rgba(23, 23, 23, 0.15)" : "none")};
`;

export const MenuContainer = styled.div`
  position: relative;
  display: inline-block;
`;

export const ThreeDotsButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(23, 23, 23, 0.08);
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    transform: scale(1.05);
  }
`;

export const Dot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%);
`;

export const DropdownMenu = styled.div<{ $openUp?: boolean; $fixed?: boolean }>`
  position: ${(p) => (p.$fixed ? "fixed" : "absolute")};
  right: ${(p) => (p.$fixed ? "auto" : "0")};
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(23, 23, 23, 0.06);
  width: 220px;
  z-index: 9999;
  overflow: hidden;
  ${(p) =>
    p.$fixed
      ? `
    margin: 0;
  `
      : p.$openUp
        ? `
    bottom: 100%;
    margin-bottom: 8px;
    top: auto;
  `
        : `
    top: 100%;
    margin-top: 8px;
  `}
`;

export const MenuItem = styled.button`
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: background 0.15s ease;
  text-align: left;

  &:hover {
    background: rgba(201, 77, 170, 0.05);
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(23, 23, 23, 0.06);
  }
`;

export const ItemIconContainer = styled.div<{ $assigned?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  flex-shrink: 0;
`;

export const ItemLabel = styled.span<{ $assigned?: boolean }>`
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #171717;
`;

export const Backdrop: React.FC<{ onClick?: () => void }> = ({ onClick }) => {
  useEffect(() => {
    if (!onClick) return;
    const handleOutsideClick = (e: MouseEvent) => {
      onClick();
    };
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [onClick]);

  return null;
};

